import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Mail } from "lucide-react";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import LuxButton from "../components/landing/LuxButton";
import LuxPill from "../components/landing/LuxPill";
import LandingFooter from "../components/landing/LandingFooter";

const SECTIONS = [
  { id: "agreement",      n: "1",  title: "Agreement to Terms" },
  { id: "license",        n: "2",  title: "License Grant" },
  { id: "accounts",       n: "3",  title: "User Accounts" },
  { id: "subscription",   n: "4",  title: "Subscription and Payments" },
  { id: "user-content",   n: "5",  title: "User Content and Data" },
  { id: "acceptable-use", n: "6",  title: "Acceptable Use Policy" },
  { id: "third-parties",  n: "7",  title: "Third-Party Integrations" },
  { id: "ip",             n: "8",  title: "Intellectual Property" },
  { id: "disclaimer",     n: "9",  title: "Disclaimer of Warranties" },
  { id: "liability",      n: "10", title: "Limitation of Liability" },
  { id: "indemnify",      n: "11", title: "Indemnification" },
  { id: "termination",    n: "12", title: "Termination" },
  { id: "law",            n: "13", title: "Governing Law" },
  { id: "changes",        n: "14", title: "Changes to Terms" },
  { id: "severability",   n: "15", title: "Severability" },
  { id: "contact",        n: "16", title: "Contact Us" },
];

const useActiveSection = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [ids]);
  return active;
};

const TermsOfServicePage = () => {
  const lastUpdated = "January 25, 2026";
  const effectiveDate = "January 25, 2026";
  const active = useActiveSection(SECTIONS.map((s) => s.id));

  return (
    <div className="lux">
      <div className="lux-prose-shell">
        {/* Mini-header */}
        <header className="lux-mini-header">
          <div className="max-w-[1200px] mx-auto px-5 lg:px-8 flex items-center justify-between h-16">
            <Link to="/" className="flex items-center"><img src="/logo.png" alt="Fieldz AI" className="h-9 object-contain" /></Link>
            <Link to="/">
              <LuxButton variant="quiet"><ArrowLeft className="w-4 h-4" /> Back</LuxButton>
            </Link>
          </div>
        </header>

        {/* Hero */}
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 pt-12 pb-8 relative z-10">
          <div className="flex items-start gap-4 max-w-[800px]">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(201, 168, 76, 0.10)", border: "1px solid rgba(201, 168, 76, 0.25)" }}
            >
              <FileText className="w-6 h-6" style={{ color: "var(--lux-gold)" }} />
            </div>
            <div>
              <LuxPill className="mb-3">Legal</LuxPill>
              <h1 className="text-4xl font-extrabold text-[var(--lux-text)] mb-2" style={{ letterSpacing: "-0.02em", lineHeight: 1.1 }} data-testid="terms-page-title">
                Terms of Service
              </h1>
              <p className="text-[14px]" style={{ color: "var(--lux-text-3)" }}>
                Effective: {effectiveDate} · Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </div>

        {/* Body — sticky TOC sidebar + content */}
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 pb-20 grid lg:grid-cols-[220px_1fr] gap-10">
          {/* TOC */}
          <aside className="hidden lg:block">
            <nav className="lux-toc" aria-label="Table of contents">
              <h2 className="lux-toc-title">On this page</h2>
              <ul className="lux-toc-list">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className={active === s.id ? "is-active" : ""} data-testid={`terms-toc-${s.id}`}>
                      {s.n}. {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <main className="lux-prose">
            <section id="agreement" className="scroll-mt-28">
              <h2>1. Agreement to Terms</h2>
              <p>These Terms of Service (&quot;Terms&quot;) constitute a legally binding agreement between you (&quot;User,&quot; &quot;you,&quot; or &quot;your&quot;) and Fieldz AI (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) governing your access to and use of the Fieldz AI platform, including any associated websites, applications, and services (collectively, the &quot;Service&quot;).</p>
              <p>By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the Service.</p>
              <p><strong>Effective Date:</strong> {effectiveDate}</p>
            </section>

            <section id="license" className="scroll-mt-28">
              <h2>2. License Grant</h2>
              <p>Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes.</p>
              <p>This license does not include the right to:</p>
              <ul>
                <li>Modify, copy, or create derivative works based on the Service</li>
                <li>Reverse engineer, decompile, or disassemble the Service</li>
                <li>Rent, lease, lend, sell, or sublicense the Service</li>
                <li>Use the Service for any unlawful purpose</li>
                <li>Remove or alter any proprietary notices on the Service</li>
              </ul>
            </section>

            <section id="accounts" className="scroll-mt-28">
              <h2>3. User Accounts</h2>
              <p>To access certain features of the Service, you must create an account. You agree to:</p>
              <ul>
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and accept all risks of unauthorized access</li>
                <li>Immediately notify us of any unauthorized use of your account</li>
                <li>Be responsible for all activities that occur under your account</li>
              </ul>
              <p>We reserve the right to suspend or terminate accounts that violate these Terms or for any other reason at our sole discretion.</p>
            </section>

            <section id="subscription" className="scroll-mt-28">
              <h2>4. Subscription and Payments</h2>
              <h3>4.1 Subscription Plans</h3>
              <p>The Service is offered through various subscription plans. The features, limitations, and pricing of each plan are described on our website. We reserve the right to modify our subscription plans at any time.</p>
              <h3>4.2 Billing</h3>
              <p>By subscribing to a paid plan, you authorize us to charge your payment method on a recurring basis. You are responsible for keeping your payment information current.</p>
              <h3>4.3 Refunds</h3>
              <p>All subscription fees are non-refundable except as required by law or as explicitly stated in these Terms. If you cancel your subscription, you will continue to have access until the end of your current billing period.</p>
              <h3>4.4 Usage Limits</h3>
              <p>Each subscription plan includes specific usage limits for features such as SMS messages, phone minutes, team members, and phone lines. Exceeding these limits may result in additional charges or service restrictions.</p>
            </section>

            <section id="user-content" className="scroll-mt-28">
              <h2>5. User Content and Data</h2>
              <h3>5.1 Your Data</h3>
              <p>You retain ownership of all data, content, and information you submit to the Service (&quot;User Data&quot;). You grant us a worldwide, non-exclusive license to use, store, and process your User Data solely for the purpose of providing the Service.</p>
              <h3>5.2 Data Responsibility</h3>
              <p>You are solely responsible for the accuracy, quality, integrity, and legality of your User Data and the means by which you acquired it. You represent that you have obtained all necessary consents to share customer information through our Service.</p>
              <h3>5.3 Data Export</h3>
              <p>You may export your User Data at any time through the Service&apos;s export functionality. Upon termination, we will provide you reasonable opportunity to export your data before deletion.</p>
            </section>

            <section id="acceptable-use" className="scroll-mt-28">
              <h2>6. Acceptable Use Policy</h2>
              <p>You agree not to use the Service to:</p>
              <ul>
                <li>Violate any applicable laws, regulations, or third-party rights</li>
                <li>Send spam, unsolicited messages, or engage in harassment</li>
                <li>Transmit viruses, malware, or other harmful code</li>
                <li>Attempt to gain unauthorized access to the Service or other systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Impersonate any person or entity</li>
                <li>Collect or store personal data about others without their consent</li>
                <li>Use the Service in any manner that could damage or overburden our infrastructure</li>
              </ul>
            </section>

            <section id="third-parties" className="scroll-mt-28">
              <h2>7. Third-Party Integrations</h2>
              <p>The Service may integrate with third-party services including but not limited to Twilio, Stripe, QuickBooks, and Google. Your use of these integrations is subject to the respective third party&apos;s terms of service and privacy policies.</p>
              <p>We are not responsible for the content, privacy practices, or availability of third-party services. Any data shared with third-party services through our integrations is subject to their terms and policies.</p>
            </section>

            <section id="ip" className="scroll-mt-28">
              <h2>8. Intellectual Property</h2>
              <p>The Service, including its original content, features, and functionality, is owned by Fieldz AI and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.</p>
              <p>Our trademarks, service marks, and logos may not be used in connection with any product or service without our prior written consent.</p>
            </section>

            <section id="disclaimer" className="scroll-mt-28">
              <h2>9. Disclaimer of Warranties</h2>
              <p style={{ textTransform: "uppercase", fontSize: 13 }}>THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.</p>
              <p>We do not warrant that the Service will be uninterrupted, timely, secure, or error-free, or that any defects will be corrected.</p>
            </section>

            <section id="liability" className="scroll-mt-28">
              <h2>10. Limitation of Liability</h2>
              <p style={{ textTransform: "uppercase", fontSize: 13 }}>TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL FIELDZ AI, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.</p>
              <p>Our total liability to you for all claims arising from or related to the Service shall not exceed the amount you paid us in the twelve (12) months preceding the claim.</p>
            </section>

            <section id="indemnify" className="scroll-mt-28">
              <h2>11. Indemnification</h2>
              <p>You agree to defend, indemnify, and hold harmless Fieldz AI and its officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or debt arising from:</p>
              <ul>
                <li>Your use of and access to the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Your User Data or content</li>
              </ul>
            </section>

            <section id="termination" className="scroll-mt-28">
              <h2>12. Termination</h2>
              <p>We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms.</p>
              <p>Upon termination, your right to use the Service will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.</p>
            </section>

            <section id="law" className="scroll-mt-28">
              <h2>13. Governing Law</h2>
              <p>These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.</p>
              <p>Any disputes arising under these Terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.</p>
            </section>

            <section id="changes" className="scroll-mt-28">
              <h2>14. Changes to Terms</h2>
              <p>We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days&apos; notice prior to any new terms taking effect.</p>
              <p>By continuing to access or use our Service after revisions become effective, you agree to be bound by the revised Terms.</p>
            </section>

            <section id="severability" className="scroll-mt-28">
              <h2>15. Severability</h2>
              <p>If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.</p>
            </section>

            <section id="contact" className="scroll-mt-28 mt-10">
              <div
                className="lux-card lux-card-glass"
                style={{ borderColor: "rgba(201, 168, 76, 0.30)", boxShadow: "0 0 32px rgba(201, 168, 76, 0.10)" }}
              >
                <h2 style={{ marginTop: 0 }}>16. Contact Us</h2>
                <p>If you have any questions about these Terms of Service, please contact us:</p>
                <div className="mt-3 space-y-2">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" style={{ color: "var(--lux-gold)" }} />
                    <a href="mailto:legal@fieldzai.com">legal@fieldzai.com</a>
                  </p>
                  <p>Fieldz AI<br />Attn: Legal Team</p>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default TermsOfServicePage;
